import { deepEqual, EventDispatcher, shallowEqual } from '@typewriter/editor';
import { getBounds, getAllBounds, deltaFromHTML, decorate, getSelection, setSelection } from '@typewriter/view';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function h(name, attributes, ...rest) {
    const children = [];
    while (rest.length) {
        const node = rest.pop();
        if (node && node.pop) {
            for (let length = node.length; length--;) {
                rest.push(node[length]);
            }
        }
        else if (node != null && node !== true && node !== false) {
            children.push(node);
        }
    }
    return typeof name === "function"
        ? name(attributes || {}, children)
        : {
            name,
            attributes: attributes || {},
            children: children
        };
}
function renderChildren(children, element) {
    patchChildren(element, children);
}
function clone(target, source) {
    var obj = {};
    for (var i in target)
        obj[i] = target[i];
    for (var i in source)
        obj[i] = source[i];
    return obj;
}
function eventListener(event) {
    return event.currentTarget.events[event.type](event);
}
function updateAttribute(element, name, value, isSvg) {
    if (name[0] === "o" && name[1] === "n") {
        if (!element.events) {
            element.events = {};
        }
        name = name.slice(2);
        const oldValue = element.events[name];
        element.events[name] = value;
        if (value) {
            if (!oldValue) {
                element.addEventListener(name, eventListener);
            }
        }
        else {
            element.removeEventListener(name, eventListener);
        }
    }
    else if (name in element && name !== "list" && !isSvg) {
        element[name] = value == null ? "" : value;
    }
    else if (value != null && value !== false) {
        element.setAttribute(name, value === true ? '' : value);
    }
    if (value == null || value === false) {
        element.removeAttribute(name);
    }
}
function createElement(node, isSvg) {
    const element = typeof node === "string" || typeof node === "number"
        ? document.createTextNode(node)
        : (isSvg = isSvg || node.name === "svg")
            ? document.createElementNS("http://www.w3.org/2000/svg", node.name)
            : document.createElement(node.name);
    const vNode = node;
    const attributes = vNode.attributes;
    if (attributes) {
        for (let i = 0; i < vNode.children.length; i++) {
            element.appendChild(createElement(vNode.children[i], isSvg));
        }
        for (let name in attributes) {
            updateAttribute(element, name, attributes[name], isSvg);
        }
    }
    return element;
}
function getElementAttributes(element, isSvg) {
    const attributes = {};
    for (let i = 0; i < element.attributes.length; i++) {
        const { name, value } = element.attributes[i];
        if (name in element && name !== "list" && !isSvg) {
            attributes[name] = element[name];
        }
        else {
            attributes[name] = value === '' ? true : value;
        }
    }
    return attributes;
}
function updateElement(element, attributes, isSvg) {
    var oldAttributes = getElementAttributes(element);
    for (let name in clone(oldAttributes, attributes)) {
        if (attributes[name] !==
            (name === "value" || name === "checked"
                ? element[name]
                : oldAttributes[name])) {
            updateAttribute(element, name, attributes[name], isSvg);
        }
    }
}
function removeElement(parent, element) {
    parent.removeChild(element);
}
function patchChildren(element, children, isSvg) {
    let i = 0;
    while (i < children.length) {
        patch(element, element.childNodes[i], children[i], isSvg);
        i++;
    }
    while (i < element.childNodes.length) {
        removeElement(element, element.childNodes[i]);
    }
}
function patch(parent, element, node, isSvg) {
    const name = element && element.nodeName !== '#text' ? element.nodeName.toLowerCase() : undefined;
    if (element == null || name !== node.name) {
        const newElement = createElement(node, isSvg);
        if (parent) {
            parent.insertBefore(newElement, element);
            if (element != null) {
                removeElement(parent, element);
            }
        }
        element = newElement;
    }
    else if (name == null) {
        if (element.nodeValue !== '' + node)
            element.nodeValue = '' + node;
    }
    else {
        updateElement(element, node.attributes, (isSvg = isSvg || node.name === "svg"));
        patchChildren(element, node.children, isSvg);
    }
    return element;
}

/*!
 * escape-html
 * Copyright(c) 2012-2013 TJ Holowaychuk
 * Copyright(c) 2015 Andreas Lubbe
 * Copyright(c) 2015 Tiancheng "Timothy" Gu
 * MIT Licensed
 */
const matchHtmlRegExp = /["'&<>]/;
function escapeHtml(string) {
    var str = '' + string;
    var match = matchHtmlRegExp.exec(str);
    if (!match) {
        return str;
    }
    var escape;
    var html = '';
    var index = 0;
    var lastIndex = 0;
    for (index = match.index; index < str.length; index++) {
        switch (str.charCodeAt(index)) {
            case 34:
                escape = '&quot;';
                break;
            case 38:
                escape = '&amp;';
                break;
            case 39:
                escape = '&#39;';
                break;
            case 60:
                escape = '&lt;';
                break;
            case 62:
                escape = '&gt;';
                break;
            default:
                continue;
        }
        if (lastIndex !== index) {
            html += str.substring(lastIndex, index);
        }
        lastIndex = index + 1;
        html += escape;
    }
    return lastIndex !== index
        ? html + str.substring(lastIndex, index)
        : html;
}

const paragraph = (attr, children) => h('p', attr.decorator, children);
const header = (attr, children) => h(`h${attr.header}`, attr.decorator, children);
const list = lists => {
    const topLevelChildren = [];
    const levels = [];
    lists.forEach(([attr, children]) => {
        const name = attr.list === 'ordered' ? 'ol' : 'ul';
        const index = attr.indent || 0;
        const item = h('li', attr.decorator, children);
        while (index >= levels.length) {
            const newLevel = h(name, { start: attr.start, type: attr.type });
            const childrenArray = levels.length ? levels[levels.length - 1].children : topLevelChildren;
            const lastChild = childrenArray[childrenArray.length - 1];
            if (typeof lastChild === 'object' && lastChild.name === 'li') {
                lastChild.children.push(newLevel);
            }
            else {
                childrenArray.push(newLevel);
            }
            levels.push(newLevel);
        }
        if (!compare(levels[index], name, attr)) {
            const newLevel = h(name, { start: attr.start, type: attr.type });
            const childrenArray = index ? levels[index - 1].children : topLevelChildren;
            childrenArray.push(newLevel);
            levels[index] = newLevel;
        }
        levels[index].children.push(item);
        levels.length = index + 1;
    });
    return topLevelChildren;
};
const blockquote = quotes => {
    let lastType, container;
    let containers = [];
    quotes.forEach(([attr, children]) => {
        if (lastType !== attr.blockquote) {
            lastType = attr.blockquote;
            container = h('blockquote', { className: `quote-${lastType}` });
            containers.push(container);
        }
        container.children.push(h('p', attr.decorator, children));
    });
    return containers;
};
const codeblock = lines => h('pre', undefined, lines.map(([attr, children]) => [children, '\n']));
const hr = () => h('hr');
list.rendersMultiple = true;
blockquote.rendersMultiple = true;
codeblock.rendersMultiple = true;
function compare(list, name, attrs) {
    return list.name === name
        && (list.attributes.start === attrs.start
            || (list.attributes.start && !attrs.start))
        && list.attributes.type === attrs.type;
}

var blocks = /*#__PURE__*/Object.freeze({
    __proto__: null,
    paragraph: paragraph,
    header: header,
    list: list,
    blockquote: blockquote,
    codeblock: codeblock,
    hr: hr
});

const bold = (attr, children) => h('strong', undefined, children);
const italic = (attr, children) => h('em', undefined, children);
const code = (attr, children) => h('code', undefined, children);
const link = (attr, children) => h('a', { href: attr.link, target: '_blank' }, children);
const decorator = (attr, children) => {
    const attrs = Object.assign({}, attr.decorator);
    attrs.class = attrs.class ? attrs.class + ' decorator' : 'decorator';
    return h('span', attrs, children);
};

var marks = /*#__PURE__*/Object.freeze({
    __proto__: null,
    bold: bold,
    italic: italic,
    code: code,
    link: link,
    decorator: decorator
});

const image = embed => {
    const { image } = embed, attrs = __rest(embed, ["image"]);
    attrs.src = image;
    return h('img', attrs);
};
const br = () => h('br');

var embeds = /*#__PURE__*/Object.freeze({
    __proto__: null,
    image: image,
    br: br
});

const components = {};
function registerComponent(name, component, rendersMultiple) {
    if (rendersMultiple != null)
        component.rendersMultiple = rendersMultiple;
    components[name] = component;
}
function getComponent(name) {
    return components[name];
}
const all = Object.assign(Object.assign(Object.assign({}, blocks), marks), embeds);
Object.keys(all).forEach(name => registerComponent(name, all[name]));

const nodeMarks = new WeakMap();
const BR = h('br');
const VOID_ELEMENTS = {
    area: true, base: true, br: true, col: true, embed: true, hr: true, img: true, input: true,
    link: true, meta: true, param: true, source: true, track: true, wbr: true
};
function deltaToVdom(delta, paper) {
    const { blocks, marks, embeds } = paper;
    const blockData = [];
    delta.eachLine((line, attributes) => {
        let inlineChildren = [];
        line.ops.forEach((op, i, array) => {
            if (op.insert) {
                let children = [];
                if (typeof op.insert === 'string') {
                    const prev = array[i - 1];
                    const next = array[i + 1];
                    let text = op.insert.replace(/  /g, ' \xA0');
                    if (!prev)
                        text = text.replace(/^ /, '\xA0');
                    if (!next || (typeof next.insert === 'string' && next.insert[0] === ' '))
                        text = text.replace(/ $/, '\xA0');
                    children.push(text);
                }
                else {
                    const embed = embeds.findByAttributes(op.insert);
                    let component;
                    if (embed && (component = getComponent(embed.name))) {
                        const node = component(op.insert);
                        children.push(node);
                    }
                }
                if (op.attributes) {
                    Object.keys(op.attributes).sort((a, b) => marks.priority(b) - marks.priority(a)).forEach(name => {
                        const mark = marks.get(name);
                        let component;
                        if (mark && (component = getComponent(mark.name))) {
                            const node = component(op.attributes, children);
                            nodeMarks.set(node, mark);
                            children = [node];
                        }
                    });
                }
                inlineChildren.push.apply(inlineChildren, children);
            }
        });
        inlineChildren = mergeChildren(inlineChildren);
        const lastChild = inlineChildren[inlineChildren.length - 1];
        if (!inlineChildren.length || (lastChild && lastChild.name === 'br')) {
            inlineChildren.push(BR);
        }
        let block = blocks.findByAttributes(attributes);
        if (!block)
            block = blocks.getDefault();
        blockData.push([block, inlineChildren, attributes]);
    });
    let blockChildren = [];
    let collect = [];
    blockData.forEach((data, i) => {
        const [block, children, attr] = data;
        const component = getComponent(block.name);
        if (component && component.rendersMultiple) {
            collect.push([attr, children]);
            const next = blockData[i + 1];
            if (!next || next[0] !== block) {
                const children = component(collect);
                blockChildren = blockChildren.concat(children);
                collect = [];
            }
        }
        else if (component) {
            const node = component(attr, children);
            blockChildren.push(node);
        }
    });
    return blockChildren;
}
function deltaToHTML(delta, paper) {
    return childrenToHTML(deltaToVdom(delta, paper));
}
function mergeChildren(oldChildren) {
    const children = [];
    oldChildren.forEach((next, i) => {
        const prev = children[children.length - 1];
        if (prev && typeof prev !== 'string' && typeof next !== 'string' && nodeMarks.get(prev) &&
            nodeMarks.get(prev) === nodeMarks.get(next) && deepEqual(prev.attributes, next.attributes)) {
            prev.children = prev.children.concat(next.children);
        }
        else {
            children.push(next);
            if (prev && typeof prev !== 'string' && prev.children) {
                prev.children = mergeChildren(prev.children);
            }
        }
    });
    if (children.length) {
        const last = children[children.length - 1];
        if (last && typeof last !== 'string' && last.children) {
            last.children = mergeChildren(last.children);
        }
    }
    return children;
}
function nodeToHTML(node) {
    const attr = Object.keys(node.attributes)
        .reduce((attr, name) => `${attr} ${escapeHtml(name)}="${escapeHtml(node.attributes[name])}"`, '');
    const children = childrenToHTML(node.children);
    const closingTag = children || !VOID_ELEMENTS[node.name] ? `</${node.name}>` : '';
    return `<${node.name}${attr}>${children}${closingTag}`;
}
function childrenToHTML(children) {
    if (!children || !children.length)
        return '';
    return children.reduce((html, child) => html + (child.name ? nodeToHTML(child) : escapeHtml(child).replace(/\xA0/g, '&nbsp;')), '');
}

const SOURCE_API = 'api';
const SOURCE_USER = 'user';
class View extends EventDispatcher {
    constructor(editor, paper, options = {}) {
        super();
        if (!editor)
            throw new Error('Editor view requires an editor');
        this.editor = editor;
        this.root = options.root || document.createElement('div');
        if (!options.root)
            this.root.className = 'typewriter-editor';
        this.paper = paper;
        this.enabled = true;
        this.enable();
        this.lastSelection = undefined;
        this._settingEditorSelection = false;
        this._settingBrowserSelection = false;
        this.modules = {};
        this.options = options;
        this.init();
    }
    get doc() {
        return this.root.ownerDocument;
    }
    getBrowserSelection() {
        return this.doc && this.doc.getSelection();
    }
    hasFocus() {
        const selection = this.getBrowserSelection();
        return !!(selection && selection.anchorNode && this.root.contains(selection.anchorNode));
    }
    focus() {
        if (this.lastSelection)
            this.editor.setSelection(this.lastSelection);
        else
            this.root.focus();
    }
    blur() {
        this.root.blur();
    }
    disable() {
        this.enable(false);
    }
    enable(enabled = true) {
        this.enabled = enabled;
        this.root.contentEditable = enabled;
    }
    getBounds(range) {
        range = this.editor.getSelectedRange(range);
        if (!range)
            return null;
        return getBounds(this.root, this.paper, range);
    }
    getAllBounds(range) {
        range = this.editor.getSelectedRange(range);
        if (!range)
            return null;
        return getAllBounds(this.root, this.paper, range);
    }
    getHTML() {
        return deltaToHTML(this.editor.contents, this.paper);
    }
    setHTML(html, source) {
        this.editor.setContents(deltaFromHTML(this.paper, html), source);
    }
    render() {
        const contents = decorate(this.root, this.editor.contents);
        const vdom = deltaToVdom(contents, this.paper);
        this.root.dispatchEvent(new Event('rendering'));
        renderChildren(vdom, this.root);
        if (this.hasFocus())
            this.updateBrowserSelection();
        this.root.dispatchEvent(new Event('render'));
    }
    updateBrowserSelection() {
        if (this._settingEditorSelection)
            return;
        this._settingBrowserSelection = true;
        this.editor.selection && this.setSelection(this.editor.selection);
        setTimeout(() => this._settingBrowserSelection = false, 20);
    }
    updateEditorSelection(source = SOURCE_API) {
        if (this._settingBrowserSelection)
            return this._settingBrowserSelection = false;
        const range = this.getSelection();
        if (range)
            this.lastSelection = range;
        this._settingEditorSelection = true;
        this.editor.setSelection(range, source);
        this._settingEditorSelection = false;
        const selection = this.getBrowserSelection();
        if (!shallowEqual(range, this.editor.selection) || (range && range[0] === range[1] && selection && selection.type === 'Range')) {
            this.updateBrowserSelection();
        }
    }
    getSelection(nativeRange) {
        return getSelection(this.root, this.paper, nativeRange);
    }
    setSelection(range) {
        setSelection(this.root, this.paper, range);
    }
    init() {
        let renderQueued = false;
        if (this.hasOwnProperty('uninit') || !this.doc)
            return;
        const doc = this.doc;
        const onSelectionChange = () => {
            this.updateEditorSelection(SOURCE_USER);
        };
        const onEditorChange = (event) => __awaiter(this, void 0, void 0, function* () {
            if (renderQueued)
                return;
            renderQueued = true;
            yield Promise.resolve();
            if (event.change)
                this.render();
            else
                this.updateBrowserSelection();
            renderQueued = false;
        });
        const rerender = () => this.render();
        doc.addEventListener('selectionchange', onSelectionChange);
        this.editor.on('editor-change', onEditorChange);
        this.editor.on('render', rerender);
        if (this.options.modules) {
            Object.keys(this.options.modules).forEach(key => this.modules[key] = this.options.modules[key](this.editor, this.root, this.paper));
        }
        this.render();
        this.uninit = () => {
            doc.removeEventListener('selectionchange', onSelectionChange);
            this.editor.off('editor-change', onEditorChange);
            this.editor.off('render', rerender);
            Object.keys(this.modules).forEach(key => {
                const api = this.modules[key];
                if (api && typeof api.onDestroy === 'function')
                    api.onDestroy();
                delete this.modules[key];
            });
            delete this.uninit;
        };
    }
    uninit() {
    }
    destroy() {
        this.uninit();
        this.fire('destroy');
    }
}

export { View, blocks, deltaToHTML, deltaToVdom, embeds, getComponent, h, marks, registerComponent };
//# sourceMappingURL=index.esm.js.map
